import {
  type LDFlagSet,
  useFlags,
  useLDClient,
} from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";

type ProgramSelectionPageFlag = {
  countries: string[] | null;
};

function get<T>(map: LDFlagSet, key: string, defaultValue: T): T {
  if (!map || typeof map !== "object") return defaultValue;
  const val = map[key];
  return typeof val === typeof defaultValue ? val : defaultValue;
}

export function useFeatureFlags() {
  const flags = useFlags();
  const ldClient = useLDClient();
  const [isTimedOut, setIsTimedOut] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsTimedOut(true);
    }, 5000);

    if (ldClient && flags && Object.keys(flags).length > 0) {
      clearTimeout(timeout);
    }

    return () => clearTimeout(timeout);
  }, [ldClient, flags]);

  const isLDReady =
    (ldClient && flags && Object.keys(flags).length > 0) || isTimedOut;

  const defaultProgramSelectionFlag: ProgramSelectionPageFlag = {
    countries: null,
  };
  const programSelectionFlag = get(
    flags,
    "part-2094-new-ib-onboarding",
    defaultProgramSelectionFlag,
  );
  const programSelectionPage = programSelectionFlag.countries;

  return {
    isLDReady,
    programSelectionPage,
    kycAndQuestionnaire: get(
      flags,
      "part-2095-onboarding-new-kyc-process-and-questionnaire",
      false,
    ),
    subIbCsvExportForMib: get(flags, "part-2804-sub-ib-csv-export", false),
    mobileUsabilityEnhancement: get(
      flags,
      "part-2304-mobile-usability-enhancement",
      false,
    ),
    referralLinks: get(flags, "part-2921-referral-links", false),
    backofficeReferralLinks: get(
      flags,
      "part-3398-backoffice-refferal-links",
      false,
    ),
    marketingBanner: get(flags, "part-3231-marketing-banners", false),
    marketingBannerSegmentTracking: get(
      flags,
      "part-3100-marketing-banner-segment-tracking",
      false,
    ),
    marketingAssets: get(flags, "part-3214-backoffice-marketing-assets", false),
    isRecentLeadsStatusV2: get(
      flags,
      "part-2773-lead-table-status-update",
      false,
    ),
    rebateTierWidget: get(flags, "part-3646-rebate-tier-widget", false),
    rebateBackofficeFilter: get(
      flags,
      "part-3766-rebate-backoffice-filter",
      false,
    ),
    performanceReport: get(flags, "part-3344-performance-report", false),
    // Shared by both Referral Links (PART-3605) and Marketing Banners
    // (PART-3857) last-click columns — independent of `performanceReport`.
    lastClickInformation: get(
      flags,
      "part-3605-add-last-click-information",
      false,
    ),
    masterSubIbReport: get(flags, "part-3829-master-sub-ib", false),
    masterSubIbPhase2: get(flags, "part-4039-master-sub-ib-phase-2", false),
    masterSubIbPhase3: get(flags, "temp-sub-ib-phase-3", false),
    masterSubOTPStepUp: get(flags, "part-4287-master-sub-otp-step-up", false),
    materializedViewHasOfferID: get(
      flags,
      "part-3795-materialized-view-hoid",
      false,
    ),
    backofficeAPI: get(flags, "part-3776-migrate-nextjs-server-action", false),
    campaigns: get(flags, "part-4165-campaigns", false),
    performanceReportV2: get(flags, "part-4355-performance-report-v2", false),
    colombiaCorDisclosure: get(
      flags,
      "part-4385-colombia-cor-disclosure",
      false,
    ),
    ibIndividualOnly: get(flags, "part-4025-ib-individual-only", false),
    ibRejectedReapply: get(flags, "part-4428-ib-rejected-reapply", false),
  };
}
